import { analyze, keyName, type Analysis } from './analyze';
import { catOf, driveSize, softSize, type PartLookup } from './catalog';
import { frontItems, type FrontLookup } from './front';
import type { Colour, Item, TextItem } from './drawing';
import { pinsW, SHEET } from './geometry';
import { ratedOf } from './generate';
import { fnOf, LIB } from './library';
import { symbolName } from './custom';
import { newId } from './tagging';
import type { LayoutBar, LayoutItem, Page, PanelLayout, Project } from './types';
import { t as $t } from './i18n';


/*
 * 2D panel layout. Everything is stored in real millimetres on the mounting
 * plate and drawn onto the A3 sheet at 1:scale, top-left of the plate at ORIGIN.
 */

export const LAYOUT_ORIGIN: [number, number] = [SHEET.X0 + 14, SHEET.Y0 + SHEET.HDR + 12];
const AREA_W = SHEET.X1 - SHEET.X0 - 28, AREA_H = SHEET.TB_Y - SHEET.Y0 - SHEET.HDR - 22;
export const LAYOUT_SCALES = [1, 2, 2.5, 5, 10, 20, 25, 50];
export const RAIL_H = 35;

export function blankLayout(w = 600, h = 800, door = false): PanelLayout {
  return { ...(door ? { door: true } : {}), w, h, scale: fitScale(w, h), rails: [], ducts: [], items: [] };
}

/** Standard cut-out for 22 mm operator devices (IEC 60947-5-1). */
export const DOOR_HOLE = 22.5;

/** Smallest standard scale at which the plate fits the drawing area. */
export function fitScale(w: number, h: number): number {
  return LAYOUT_SCALES.find((s) => w / s <= AREA_W && h / s <= AREA_H) ?? 100;
}

/** Plate mm → sheet mm. */
export const toSheet = (l: PanelLayout, x: number, y: number): [number, number] => [LAYOUT_ORIGIN[0] + x / l.scale, LAYOUT_ORIGIN[1] + y / l.scale];
/** Sheet mm → plate mm. */
export const toPlate = (l: PanelLayout, x: number, y: number): [number, number] => [(x - LAYOUT_ORIGIN[0]) * l.scale, (y - LAYOUT_ORIGIN[1]) * l.scale];

/** Device types that are mounted outside the cabinet and not offered for the plate. */
/** Distance between the conductors of a main busbar system on the plate (mm). */
export const BAR_PITCH = 60;
export const BAR_NAMES = ['L1', 'L2', 'L3', 'N', 'PE'];
/** Copper busbar per phase for a current: painted bars, 35 °C (after DIN 43671), one to three bars in parallel. */
const BUSBARS: [number, string][] = [
  [325, '20×5'], [450, '30×5'], [560, '40×5'], [700, '50×5'], [850, '40×10'], [1000, '50×10'], [1150, '60×10'], [1450, '80×10'], [1750, '100×10'],
  [2350, '2×80×10'], [2800, '2×100×10'], [3700, '3×100×10'], [4600, '4×100×10'], [5400, '4×120×10'], [6600, '4×160×10'],
];
export function busbarSize(amps: number): string {
  return (BUSBARS.find(([a]) => amps <= a) ?? [0, '4×160×10'])[1];
}
/** Current a busbar of the table carries (painted, 35 °C, after DIN 43671); undefined for a size not in it. */
export const busbarAmps = (spec: string): number | undefined => BUSBARS.find(([, s]) => s === spec)?.[0];
/** Air clearance kept between the copper of two phases (packs of parallel bars), mm. */
export const PHASE_CLEAR = 30;
/** Conductor spacing of a busbar system: the standard 60 mm, more when parallel bars are thicker. */
export const barPitch = (spec: string) => Math.max(BAR_PITCH, barThickness(spec) + PHASE_CLEAR);
/** Offsets of the parallel bars of one phase across their pack: a bar-thick gap between them. */
export const strips = (spec: string) => {
  const d = busbarDims(spec);
  return Array.from({ length: d.n }, (_, i) => i * 2 * d.t);
};
/** Bars, width and thickness of a busbar designation like "2×80×10". */
export function busbarDims(spec: string): { n: number; w: number; t: number } {
  const v = spec.split('×').map(Number);
  return v.length === 3 ? { n: v[0], w: v[1], t: v[2] } : { n: 1, w: v[0] || 20, t: v[1] || 5 };
}
/** Height of a (parallel) busbar seen edge-on from the front: the bars with a bar-thick gap between them. */
export const barThickness = (spec: string) => {
  const d = busbarDims(spec);
  return d.n * d.t + (d.n - 1) * d.t;
};

// devices out in the field, not in the cabinet: motors, the generator, sensors and valves (and any symbol marked
// field, such as a thermostat on a PLC input; a thermostat without the mark is the panel's own)
const FIELD = new Set(['motor3', 'motor6', 'gen3', 'xmtr', 'ls_no', 'ls_nc', 'vprop', 'sol', 'fs_no', 'fs_nc', 'flow_no', 'flow_nc', 'ps_no', 'ps_nc', 'prox_no', 'prox_nc', 'ind_no', 'ind_nc']);

export interface LayoutDevice {
  key: string;
  name: string;
  desc: string;
  pn: string;
  w: number;
  h: number;
  /** size came from the parts database (false = default size) */
  sized: boolean;
  strip: boolean;
  count: number;
  /** weight in kg from the parts database */
  kg?: number;
  /** main poles from the parts database (4: with neutral) */
  poles?: number;
  /** rated current from the part's description (breakers), A */
  amps?: number;
  /** mounting depth in mm: the part's, or the typical one of a drive or soft starter for its power */
  dp?: number;
}

/** Terminal feet of a breaker across its width: N first (left) on a 4 pole one, then L1 L2 L3. */
export function feet(x: number, d: LayoutDevice): { x: number; ph: 'N' | 'L1' | 'L2' | 'L3' }[] {
  const four = d.poles === 4, n = four ? 4 : 3;
  return (four ? (['N', 'L1', 'L2', 'L3'] as const) : (['L1', 'L2', 'L3'] as const)).map((ph, k) => ({ x: x + (d.w * (k + 1)) / (n + 1), ph }));
}
/** The phase feet L1 L2 L3 only. */
export const phaseFeet = (x: number, d: LayoutDevice) => feet(x, d).filter((f) => f.ph !== 'N').map((f) => f.x);
/** Copper on a breaker's terminals, by its rated current: as many bars in parallel as the busbar of that current. */
export const footSpec = (d: LayoutDevice) => busbarSize(d.amps ?? 630);
/** Rear terminals of a breaker: near its top and bottom edge (from the top of the device), mm. */
export const terminalYs = (d: LayoutDevice): [number, number] => {
  const k = Math.min(60, d.h * 0.15);
  return [k, d.h - k];
};

/** Devices of the project that can be placed on a plate, with their mounting size. */
export function layoutDevices(A: Analysis, parts: PartLookup = catOf): Map<string, LayoutDevice> {
  const out = new Map<string, LayoutDevice>();
  for (const it of A.all) {
    const L = LIB[it.s.type], key = A.devKey.get(it.s.id);
    if (!key || FIELD.has(fnOf(it.s.type)) || it.s.props.field === '1' || !['main', 'plc', 'term'].includes(L.role)) continue;
    const p = it.s.part ? parts(it.s.part) : undefined;
    const d = out.get(key);
    if (L.role === 'term') {
      // a terminal strip is one item whose width is the sum of its terminals
      const w = p?.w ?? 5.2, h = p?.h ?? 42;
      if (d) {
        d.w += w;
        d.h = Math.max(d.h, h);
        d.count++;
        d.sized &&= !!p?.w;
        if (p?.kg != null) d.kg = (d.kg ?? 0) + p.kg;
      } else out.set(key, { key, name: keyName(A, key), desc: $t("Klemens şeridi"), pn: it.s.part, w, h, sized: !!p?.w, strip: true, count: 1, ...(p?.kg != null ? { kg: p.kg } : {}) });
      continue;
    }
    if (d) {
      d.count++;
      if (!d.sized && p?.w) Object.assign(d, { w: p.w, h: p.h ?? d.h, pn: it.s.part, sized: true, ...(p.poles ? { poles: p.poles } : {}), ...(p.dp ? { dp: p.dp } : {}), ...(ratedOf(p.d) ? { amps: ratedOf(p.d)! } : {}) });
      if (d.kg == null && p?.kg != null) d.kg = p.kg;
      continue;
    }
    let dflt = L.role === 'plc' ? [35, 100] : ['pb_no', 'pb_nc', 'pb_latch', 'estop', 'estop_no', 'rot01', 'rot012', 'lamp'].includes(fnOf(it.s.type)) ? [30, 30] : [45, 80];
    // drives and soft starters: the typical size for the motor power until a part with a size is chosen
    const kw = Number(it.s.props.kw) || Number(/(\d+(?:[.,]\d+)?)\s*kW/.exec(p?.d ?? '')?.[1].replace(',', '.')) || 0;
    let dp = p?.dp;
    if (kw && (it.s.type === 'vfd' || it.s.type === 'ss')) {
      const f = (it.s.type === 'vfd' ? driveSize : softSize)(kw);
      dflt = [f.w, f.h];
      dp ??= f.dp;
    }
    out.set(key, {
      key, name: keyName(A, key), desc: it.s.desc || symbolName(it.s.type), pn: it.s.part,
      w: p?.w ?? dflt[0], h: p?.h ?? dflt[1], sized: !!p?.w, strip: false, count: 1, ...(p?.kg != null ? { kg: p.kg } : {}), ...(dp ? { dp } : {}), ...(p?.poles ? { poles: p.poles } : {}), ...(p && ratedOf(p.d) ? { amps: ratedOf(p.d)! } : {}),
    });
  }
  for (const d of out.values()) d.w = Math.round(d.w * 10) / 10;
  return out;
}

export function placedKeys(pr: Project): Set<string> {
  return new Set(pr.pages.flatMap((p) => (p.kind === 'layout' ? p.layout?.items.map((i) => i.dev) ?? [] : [])));
}

/** Places a device on a rail if its centre is close to one; returns the snapped top-left. */
export function snapToRail(l: PanelLayout, x: number, y: number, h: number, tol = Math.max(12, h / 2)): [number, number] {
  const cy = y + h / 2;
  let best: number | null = null;
  for (const r of l.rails) if (Math.abs(r.y - cy) <= tol && x + 1 >= r.x - 1 && x <= r.x + r.len) if (best == null || Math.abs(r.y - cy) < Math.abs(best - cy)) best = r.y;
  return [Math.round(x), best != null ? Math.round((best - h / 2) * 10) / 10 : Math.round(y)];
}

export function addLayoutItem(l: PanelLayout, dev: LayoutDevice, x: number, y: number): LayoutItem {
  const [sx, sy] = snapToRail(l, x, y, dev.h);
  const it = { id: newId(), dev: dev.key, x: sx, y: sy };
  l.items.push(it);
  return it;
}

type Rect = [number, number, number, number];
const overlap = (a: Rect, b: Rect) => a[0] < b[2] - 0.01 && b[0] < a[2] - 0.01 && a[1] < b[3] - 0.01 && b[1] < a[3] - 0.01;

export interface LayoutIssue {
  sev: 'err' | 'warn';
  msg: string;
  pi: number;
}

export function layoutIssues(pr: Project, A: Analysis, parts: PartLookup = catOf): LayoutIssue[] {
  const devs = layoutDevices(A, parts), out: LayoutIssue[] = [];
  const seen = new Map<string, number>();
  pr.pages.forEach((page, pi) => {
    const l = page.kind === 'layout' ? page.layout : undefined;
    if (!l) return;
    const rects = l.items.map((i) => {
      const d = devs.get(i.dev);
      return { i, d, r: [i.x, i.y, i.x + (d?.w ?? 0), i.y + (d?.h ?? 0)] as Rect };
    });
    for (const { i, d, r } of rects) {
      const nm = d?.name ?? keyName(A, i.dev);
      if (!d) {
        out.push({ sev: 'warn', msg: $t("{0}: pano yerleşimindeki cihaz şemada yok", nm), pi });
        continue;
      }
      if (seen.has(i.dev)) out.push({ sev: 'err', msg: $t("{0}: pano yerleşiminde birden fazla kez yerleştirilmiş", nm), pi });
      seen.set(i.dev, pi);
      if (r[0] < 0 || r[1] < 0 || r[2] > l.w || r[3] > l.h) out.push({ sev: 'err', msg: $t("{0}: montaj plakasının dışına taşıyor", nm), pi });
      for (const du of l.ducts) if (overlap(r, [du.x, du.y, du.x + du.w, du.y + du.h])) out.push({ sev: 'err', msg: $t("{0}: kablo kanalıyla çakışıyor", nm), pi });
      // the copper packs on neighbouring poles keep the air clearance between phases
      if (!l.door && footed(A, d)) {
        const fs = footSpec(d), f = feet(i.x, d), gapMm = f.length > 1 ? Math.round(f[1].x - f[0].x - barThickness(fs)) : Infinity;
        if (gapMm < PHASE_CLEAR) out.push({ sev: 'warn', msg: $t('{0}: kutuplar arasında bakır ({1}) için {2} mm aralık kalıyor, en az {3} mm olmalı; bakırları kutup dışında ayırın ya da daha geniş gövde seçin', nm, fs, gapMm, PHASE_CLEAR), pi });
      }
      // on the door only the cut-out matters, not the device size
      if (!d.sized && !l.door) out.push({ sev: 'warn', msg: $t("{0}: parça ölçüsü yok, varsayılan {1}×{2} mm kullanıldı", nm, d.w, d.h), pi });
    }
    for (let a = 0; a < rects.length; a++)
      for (let b = a + 1; b < rects.length; b++)
        if (rects[a].d && rects[b].d && overlap(rects[a].r, rects[b].r))
          out.push({ sev: 'err', msg: $t("{0} ile {1} üst üste biniyor", rects[a].d!.name, rects[b].d!.name), pi });
    if (!l.door)
      sectionWeights(l, devs).forEach((kg, n) => {
        if (kg > SECTION_MAX_KG) out.push({ sev: 'warn', msg: $t('Göz {0}: cihaz ağırlığı {1} kg, bir göz için {2} kg üstü', n + 1, Math.round(kg), SECTION_MAX_KG), pi });
      });
  });
  return out;
}

/** Drawing of a layout page (without frame). */
export function layoutItems(pr: Project, A: Analysis, pi: number, parts: PartLookup = catOf, fronts?: FrontLookup): Item[] {
  const l = pr.pages[pi].layout;
  if (!l) return [];
  const out: Item[] = [], devs = layoutDevices(A, parts), k = 1 / l.scale;
  const rect = (x: number, y: number, w: number, h: number, stroke: Colour, width = 0.3, fill?: Colour): void => {
    const [a, b] = toSheet(l, x, y);
    out.push({ k: 'poly', pts: [[a, b], [a + w * k, b], [a + w * k, b + h * k], [a, b + h * k]], closed: true, stroke, fill, width, layer: 'symbol' });
  };
  const text = (x: number, y: number, t: string, size: number, o: Partial<TextItem> = {}) => {
    const [a, b] = toSheet(l, x, y);
    out.push({ k: 'text', x: a, y: b, size, text: t, anchor: 'middle', font: 'mono', colour: 'ink', layer: 'label', ...o });
  };
  // plate + overall dimensions
  rect(0, 0, l.w, l.h, 'ink', 0.5);
  const what = l.inside ? $t("Pano kapağı · iç görünüş") : l.door ? $t("Pano kapağı · ön görünüş") : $t("Montaj plakası");
  const [px, py] = toSheet(l, 0, 0), [qx, qy] = toSheet(l, l.w, l.h);
  out.push({ k: 'poly', pts: [[px, py - 4], [qx, py - 4]], stroke: 'ink2', width: 0.18, layer: 'frame' });
  out.push({ k: 'poly', pts: [[qx + 4, py], [qx + 4, qy]], stroke: 'ink2', width: 0.18, layer: 'frame' });
  out.push({ k: 'text', x: (px + qx) / 2, y: py - 5, size: 2.5, text: `${l.w} mm`, anchor: 'middle', font: 'mono', colour: 'ink2', layer: 'frame' });
  out.push({ k: 'text', x: qx + 5.5, y: (py + qy) / 2, size: 2.5, text: `${l.h} mm`, anchor: 'middle', font: 'mono', colour: 'ink2', rot: 90, layer: 'frame' });
  out.push({ k: 'text', x: px, y: qy + 5, size: 2.6, text: $t("{0} {1} × {2} mm · Ölçek 1:{3}", what, l.w, l.h, l.scale), anchor: 'start', font: 'ui', colour: 'ink2', layer: 'frame' });
  if (l.inside) {
    // the door from inside: the devices of its front view mirrored left to right, their bodies seen from behind
    const src = pr.pages.find((p) => p.id === l.inside)?.layout;
    for (const i of src?.items ?? []) {
      const d = devs.get(i.dev), w = d?.w ?? 20, h = d?.h ?? 20, x = l.w - i.x - w;
      rect(x, i.y, w, h, 'ink', 0.35, 'paper');
      text(x + w / 2, i.y + h / 2 + 0.7 * l.scale, d?.name ?? keyName(A, i.dev), 2.2, { bold: true });
      if (d?.desc) text(x + w / 2, i.y + h + 2.6 * l.scale, d.desc, 1.8, { font: 'ui', colour: 'ink2' });
    }
    return out;
  }
  // compartments: dividing lines and, under the plate, number, width and device weight of each
  if (l.sections && l.sections.length > 1 && !l.door) {
    const kgs = sectionWeights(l, devs);
    let sx = 0;
    l.sections.forEach((w, n) => {
      if (n) {
        const [a, b] = toSheet(l, sx, 0), [, e] = toSheet(l, sx, l.h);
        out.push({ k: 'poly', pts: [[a, b], [a, e]], stroke: 'ink', width: 0.5, layer: 'symbol' });
      }
      const [cx] = toSheet(l, sx + w / 2, 0);
      out.push({ k: 'text', x: cx, y: qy + 9.5, size: 2.3, text: $t('Göz {0} · {1} mm · ≈ {2} kg', n + 1, w, Math.round(kgs[n])), anchor: 'middle', font: 'ui', colour: 'ink2', layer: 'frame' });
      const nm = l.sectionNames?.[n];
      if (nm) out.push({ k: 'text', x: cx, y: qy + 13.5, size: 2.3, text: nm, anchor: 'middle', font: 'ui', colour: 'ink', layer: 'frame' });
      sx += w;
    });
  }
  // wire ducts with slot lines
  for (const d of l.ducts) {
    rect(d.x, d.y, d.w, d.h, 'ink2', 0.25);
    const along = d.w >= d.h, n = 2;
    for (let i = 1; i <= n; i++) {
      const [a, b] = toSheet(l, along ? d.x : d.x + (d.w * i) / (n + 1), along ? d.y + (d.h * i) / (n + 1) : d.y);
      const [c, e] = toSheet(l, along ? d.x + d.w : d.x + (d.w * i) / (n + 1), along ? d.y + (d.h * i) / (n + 1) : d.y + d.h);
      out.push({ k: 'poly', pts: [[a, b], [c, e]], stroke: 'ink2', width: 0.15, dash: [1.2, 1], layer: 'symbol' });
    }
  }
  // main busbars: L1 L2 L3 N PE edge-on one below the other, or (floor-standing board) the phases in the middle
  // at the same height and N, PE along the bottom; with their size
  const fed = l.bars?.length ? busFed(pr, A) : () => false;
  for (const bar of l.bars ?? []) {
    if (!bar.mid) {
      BAR_NAMES.forEach((nm, k) => {
        const spec = nm === 'PE' ? bar.pe : bar.spec, th = barThickness(spec), y = bar.y + k * barPitch(bar.spec);
        rect(bar.x, y - th / 2, bar.w, th, 'ink', 0.35);
        text(bar.x + 8 * l.scale, y - th / 2 - 1.2 * l.scale, `${nm} · Cu ${spec}`, 2, { anchor: 'start', colour: 'ink2' });
      });
      continue;
    }
    const bw = busbarDims(bar.spec).w, th = barThickness(bar.spec);
    rect(bar.x, bar.y - bw / 2, bar.w, bw, 'ink', 0.4, 'paper');
    text(bar.x + 8 * l.scale, bar.y + 0.7 * l.scale, $t('L1 L2 L3 aynı hizada, derinlikte sıralı · Cu {0}', bar.spec), 2, { anchor: 'start', colour: 'ink2' });
    for (const [nm, y, spec, span] of [['N', bar.mid.nY, bar.n ?? bar.spec, bar.mid.nSpan], ['PE', bar.mid.peY, bar.pe, bar.mid.peSpan]] as const) {
      const t2 = barThickness(spec), [x0, x1] = span ?? [bar.x, bar.x + bar.w];
      rect(x0, y - t2 / 2, x1 - x0, t2, 'ink', 0.35);
      text(x0 + 8 * l.scale, y - t2 / 2 - 1.2 * l.scale, `${nm} · Cu ${spec}`, 2, { anchor: 'start', colour: 'ink2' });
    }
    // copper from the phase bars to the feet of the breakers on the busbar circuit
    for (const i of l.items) {
      const d = devs.get(i.dev);
      if (!d || !fed(i.dev) || !onBar(bar, i.x + d.w / 2)) continue;
      const foot = footed(A, d) ? FOOT : 0, down = i.y > bar.y;
      const y0 = down ? bar.y + bw / 2 : i.y + d.h + foot, y1 = down ? i.y - foot : bar.y - bw / 2;
      const fs = footed(A, d) ? footSpec(d) : bar.spec, ft = barThickness(fs), st = busbarDims(fs).t;
      if (y1 > y0) for (const x of phaseFeet(i.x, d)) for (const o of strips(fs)) rect(x - ft / 2 + o, y0, st, y1 - y0, 'ink', 0.3);
    }
  }
  // connection bars (bus coupler to the next busbar): the three phases one behind the other, drawn as one run
  for (const k of l.links ?? []) {
    const t = k.kind === 'N' ? Math.max(barThickness(k.spec), 10) : busbarDims(k.spec).w / 2;
    for (let n = 0; n < k.pts.length - 1; n++) {
      const [p, q] = [k.pts[n], k.pts[n + 1]];
      const x0 = Math.min(p[0], q[0]), y0 = Math.min(p[1], q[1]);
      if (p[1] === q[1]) rect(x0 - t / 2, y0 - t / 2, Math.abs(q[0] - p[0]) + t, t, 'ink', 0.4, 'paper');
      else rect(x0 - t / 2, y0 - t / 2, t, Math.abs(q[1] - p[1]) + t, 'ink', 0.4, 'paper');
    }
    if (k.pts.length < 3 || k.kind || (k.ph ?? 0) > 0) continue;
    const [a, b] = [k.pts[1], k.pts[2]];
    text((a[0] + b[0]) / 2, a[1] + t + 2.2 * l.scale, $t('Kuplaj bağlantısı L1 L2 L3 · Cu {0}', k.spec), 1.8, { colour: 'ink2' });
  }
  // busbar supports: the phase carrier around the bars, the N / PE carrier at the bottom
  for (const { x, bar } of barSupports(l)) {
    const bw = busbarDims(bar.spec).w;
    rect(x - SUPPORT_W / 2, bar.y - bw / 2 - 15, SUPPORT_W, bw + 30, 'ink2', 0.35);
    rect(x - SUPPORT_W / 2, bar.mid!.nY - 15, SUPPORT_W, bar.mid!.peY - bar.mid!.nY + 30, 'ink2', 0.35);
  }
  // cross beams under heavy breakers
  for (const b of l.beams ?? []) {
    rect(b.x, b.y, b.w, BEAM_H, 'ink2', 0.3);
    text(b.x + 3 * l.scale, b.y + BEAM_H / 2 + 0.6 * l.scale, $t('Atkı'), 1.6, { anchor: 'start', colour: 'ink2', font: 'ui' });
  }
  // heavy breakers: copper feet above and below, the cable space under the lower feet
  if (!l.door)
    for (const i of l.items) {
      const d = devs.get(i.dev);
      if (!d || !footed(A, d)) continue;
      // from the rear terminals near the top and bottom edge: the parallel bars of each pole, out beyond the
      // breaker (seen) and behind it (hidden, dashed)
      const fs = footSpec(d), ft = barThickness(fs), st = busbarDims(fs).t, [ty, by] = terminalYs(d);
      for (const f of feet(i.x, d))
        for (const o of strips(fs)) {
          const x = f.x - ft / 2 + o;
          rect(x, i.y - FOOT, st, FOOT, 'ink', 0.3);
          rect(x, i.y + d.h, st, FOOT, 'ink', 0.3);
          for (const [ya, yb] of [[i.y, i.y + ty], [i.y + by, i.y + d.h]]) {
            const [a, b] = toSheet(l, x, ya), [c, e] = toSheet(l, x + st, yb);
            out.push({ k: 'poly', pts: [[a, b], [c, b], [c, e], [a, e]], closed: true, stroke: 'ink2', width: 0.15, dash: [0.8, 0.6], layer: 'symbol' });
          }
        }
      // a coupler's bottom feet take a connection bar, not cables
      if (l.links?.some((k) => Math.abs(k.pts[0][1] - (i.y + d.h + FOOT)) < 1 && k.pts[0][0] > i.x && k.pts[0][0] < i.x + d.w)) continue;
      // over the busbars the cables come in from above, under them from below
      const midBar = l.bars?.find((x) => x.mid), up = !!midBar && i.y + d.h < midBar.y;
      const cy = up ? i.y - FOOT - CABLE_GAP : i.y + d.h + FOOT;
      const [a, b] = toSheet(l, i.x, cy), [c, e] = toSheet(l, i.x + d.w, cy + CABLE_GAP);
      out.push({ k: 'poly', pts: [[a, b], [c, b], [c, e], [a, e]], closed: true, stroke: 'ink2', width: 0.18, dash: [1.5, 1], layer: 'symbol' });
      text(i.x + d.w / 2, cy + CABLE_GAP / 2, $t('Kablo bağlantı boşluğu'), 1.7, { font: 'ui', colour: 'ink2' });
    }
  // DIN rails
  for (const r of l.rails) {
    rect(r.x, r.y - RAIL_H / 2, r.len, RAIL_H, 'ink2', 0.2);
    const [a, b] = toSheet(l, r.x, r.y), [c] = toSheet(l, r.x + r.len, r.y);
    out.push({ k: 'poly', pts: [[a, b], [c, b]], stroke: 'ink2', width: 0.15, dash: [2, 1], layer: 'symbol' });
  }
  // devices
  for (const i of l.items) {
    const d = devs.get(i.dev);
    const w = d?.w ?? 20, h = d?.h ?? 20;
    if (l.door) {
      // door: front outline, the cut-out with its centre lines, tag and description below
      const cx = i.x + w / 2, cy = i.y + h / 2, [a, b] = toSheet(l, cx, cy), cut = doorCut(d);
      rect(i.x, i.y, w, h, 'ink2', 0.2);
      let r: number;
      if (cut && 'dia' in cut) {
        r = (cut.dia / 2) * k;
        out.push({ k: 'circle', c: [a, b], r, stroke: 'ink', width: 0.35, layer: 'symbol' });
      } else if (cut) {
        r = (Math.max(cut.w, cut.h) / 2) * k;
        rect(cx - cut.w / 2, cy - cut.h / 2, cut.w, cut.h, 'ink', 0.35);
        text(cx, cy - cut.h / 2 + 3 * l.scale, cutText(cut), 1.8, { colour: 'ink2' });
      } else {
        // the maker's cut-out: the front outline dashed, the size to be taken from the device's data
        r = (Math.max(w, h) / 2) * k;
        const [p0, q0] = toSheet(l, i.x + 2, i.y + 2), [p1, q1] = toSheet(l, i.x + w - 2, i.y + h - 2);
        out.push({ k: 'poly', pts: [[p0, q0], [p1, q0], [p1, q1], [p0, q1]], closed: true, stroke: 'ink', width: 0.3, dash: [1.5, 0.8], layer: 'symbol' });
        text(cx, cy - h / 2 + 4 * l.scale, $t("Kesim: üretici verisi"), 1.8, { font: 'ui', colour: 'ink2' });
      }
      out.push({ k: 'poly', pts: [[a - r - 1, b], [a + r + 1, b]], stroke: 'ink2', width: 0.13, dash: [1.5, 0.8], layer: 'symbol' });
      out.push({ k: 'poly', pts: [[a, b - r - 1], [a, b + r + 1]], stroke: 'ink2', width: 0.13, dash: [1.5, 0.8], layer: 'symbol' });
      text(cx, i.y + h + 2.2 * l.scale, d?.name ?? keyName(A, i.dev), 2.2, { bold: true });
      if (d?.desc) text(cx, i.y + h + 4.6 * l.scale, d.desc, 1.8, { font: 'ui', colour: 'ink2' });
      continue;
    }
    const front = d?.pn && !d.strip ? fronts?.(d.pn) : undefined;
    if (front) {
      // real front view: white backing, the drawing, and a small tag in the top-left corner
      rect(i.x, i.y, w, h, 'frame', 0.1, 'paper');
      const [a, b] = toSheet(l, i.x, i.y);
      out.push(...frontItems(front, d!.pn, a, b, w * k, h * k));
      const name = d!.name, ts = Math.max(1.2, Math.min(2.2, (w * k) / Math.max(3, name.length * 0.7)));
      out.push({ k: 'poly', pts: [[a, b], [a + name.length * ts * 0.62 + 0.8, b], [a + name.length * ts * 0.62 + 0.8, b + ts + 0.6], [a, b + ts + 0.6]], closed: true, fill: 'paper', stroke: 'accent', width: 0.12, layer: 'label' });
      out.push({ k: 'text', x: a + 0.4, y: b + ts * 0.85 + 0.2, size: ts, text: name, anchor: 'start', font: 'mono', bold: true, colour: 'accent', layer: 'label' });
      continue;
    }
    rect(i.x, i.y, w, h, d ? 'ink' : 'accent', 0.35, 'paper');
    const size = Math.max(1.4, Math.min(3, (w * k) / Math.max(3, (d?.name.length ?? 4) * 0.62)));
    const vertical = d?.strip ? false : w * k < 7;
    // centred label; +0.35 × size moves the baseline so the text sits in the middle
    const [lx, ly] = toSheet(l, i.x + w / 2, i.y + h / 2);
    out.push({
      k: 'text', x: vertical ? lx + size * 0.35 : lx, y: vertical ? ly : ly + size * 0.35, size, text: d?.name ?? keyName(A, i.dev),
      anchor: 'middle', font: 'mono', bold: true, colour: 'ink', rot: vertical ? -90 : undefined, layer: 'label',
    });
    if (d?.pn && h * k > 12 && w * k > 10) text(i.x + w / 2, i.y + h / 2 + 3 * l.scale, d.pn, Math.min(2, size * 0.75), { colour: 'ink2', font: 'ui' });
  }
  if (l.door && l.items.length) {
    // cut-out positions: ordinate dimensions of the hole centres from the top-left corner, x along the top edge and y
    // left of the door; the round holes' size once in the footer
    const xs = new Set<number>(), ys = new Set<number>();
    let round = false;
    for (const i of l.items) {
      const d = devs.get(i.dev);
      xs.add(Math.round((i.x + (d?.w ?? 20) / 2) * 10) / 10);
      ys.add(Math.round((i.y + (d?.h ?? 20) / 2) * 10) / 10);
      const c = doorCut(d);
      round ||= !!c && 'dia' in c;
    }
    const nf = (v: number) => String(v).replace('.', ',');
    for (const x of xs) {
      const [a] = toSheet(l, x, 0);
      // inside the top edge (the sheet has no room above the door; door devices start 100 mm down)
      out.push({ k: 'poly', pts: [[a, py], [a, py + 2]], stroke: 'ink2', width: 0.13, layer: 'frame' });
      out.push({ k: 'text', x: a + 0.7, y: py + 2.5, size: 1.8, text: nf(x), anchor: 'end', font: 'mono', colour: 'ink', rot: -90, layer: 'frame' });
    }
    for (const y of ys) {
      const [, b] = toSheet(l, 0, y);
      out.push({ k: 'poly', pts: [[px, b], [px - 7, b]], stroke: 'ink2', width: 0.13, layer: 'frame' });
      out.push({ k: 'text', x: px - 7.5, y: b + 0.7, size: 1.8, text: nf(y), anchor: 'end', font: 'mono', colour: 'ink', layer: 'frame' });
    }
    out.push({ k: 'poly', pts: [[px - 2, py - 2], [px + 2, py + 2]], stroke: 'ink', width: 0.25, layer: 'frame' });
    out.push({ k: 'text', x: px, y: qy + 9, size: 2.2, text: $t("Delik merkezleri sol üst köşeden, mm{0}", round ? $t(" · yuvarlak delikler Ø22,5") : ''), anchor: 'start', font: 'ui', colour: 'ink2', layer: 'frame' });
  }
  return out;
}

/* ---------------- door ---------------- */

/** Operator devices on the door in schematic order (a motor's buttons and lamps stay together), 60 mm apart, rows 80 mm apart. */
function doorLayout(l: PanelLayout, A: Analysis, todo: LayoutDevice[], res: AutoLayoutResult, grow: boolean): AutoLayoutResult {
  const PX = 60, PY = 80, top = Math.max(100, ...l.items.map((i) => i.y + 80));
  const pos = (k: string) => {
    const it = A.mains.get(k)?.[0];
    return it ? it.pi * 1000 + it.s.x : 1e9;
  };
  const list = [...todo].sort((a, b) => pos(a.key) - pos(b.key));
  const cols = Math.max(1, Math.floor((l.w - 120) / PX) + 1);
  list.forEach((d, n) => {
    const r = Math.floor(n / cols), c = n % cols, cy = top + r * PY;
    // each row centred on the door
    const inRow = Math.min(cols, list.length - r * cols), x0 = (l.w - (inRow - 1) * PX) / 2;
    // wider than the door, or below its bottom edge (a door grows only when asked to)
    if (d.w > l.w - 40) return void res.skipped.push(d.name);
    if (cy + d.h / 2 + 40 > l.h) {
      if (!grow) return void res.skipped.push(d.name);
      l.h = Math.ceil((cy + d.h / 2 + 60) / 50) * 50;
      res.grewTo = l.h;
    }
    l.items.push({ id: newId(), dev: d.key, x: Math.round((x0 + c * PX - d.w / 2) * 10) / 10, y: Math.round((cy - d.h / 2) * 10) / 10 });
    res.placed.push(d.name);
    res.rows = Math.max(res.rows, r + 1);
  });
  if (res.grewTo) l.scale = Math.max(l.scale, fitScale(l.w, l.h));
  return res;
}

/** Panel cut-outs of the DIN 43700 front sizes (meters, controllers 48…144 mm square), mm. */
const DIN_CUT: Record<number, number> = { 48: 45, 72: 68, 96: 92, 144: 138 };
/**
 * Cut-out of a door device: a Ø 22.5 hole for 22 mm operator devices (buttons, lamps, selectors), the DIN 43700
 * square for square fronts of 48…144 mm, otherwise its size comes from the maker's data (null).
 */
export function doorCut(d: { w: number; h: number } | undefined): { dia: number } | { w: number; h: number } | null {
  if (!d || (d.w <= 40 && d.h <= 40)) return { dia: DOOR_HOLE };
  const side = Math.round(d.w);
  if (Math.abs(d.w - d.h) < 1 && DIN_CUT[side]) return { w: DIN_CUT[side], h: DIN_CUT[side] };
  return null;
}
/** A cut-out as text: "Ø22,5", "92 × 92", or empty when the maker's data decides. */
export const cutText = (c: ReturnType<typeof doorCut>) => (!c ? '' : 'dia' in c ? 'Ø' + String(c.dia).replace('.', ',') : `${c.w} × ${c.h}`);

/** Cut-outs of all door pages: centre measured from the top-left corner of the door. */
export function doorHoles(pr: Project, A: Analysis, parts: PartLookup = catOf) {
  const devs = layoutDevices(A, parts), out: { pi: number; name: string; desc: string; cut: ReturnType<typeof doorCut>; x: number; y: number }[] = [];
  pr.pages.forEach((p, pi) => {
    if (!p.layout?.door || p.layout.inside) return;
    for (const i of p.layout.items) {
      const d = devs.get(i.dev);
      out.push({ pi, name: d?.name ?? keyName(A, i.dev), desc: d?.desc ?? '', cut: doorCut(d), x: Math.round((i.x + (d?.w ?? 0) / 2) * 10) / 10, y: Math.round((i.y + (d?.h ?? 0) / 2) * 10) / 10 });
    }
  });
  return out;
}

/** DIN rail and wire duct lengths of all mounting plates (for the bill of materials). */
/** Busbar support width along the bar, and its distance from a compartment wall, mm. */
export const SUPPORT_W = 30, SUPPORT_IN = 50;
/**
 * Insulating supports of a floor-standing board's busbars: in every compartment near both walls (clear of the
 * breakers in the middle), one carrier for the three phase bars and one for N and PE at the bottom.
 */
export function barSupports(l: PanelLayout): { x: number; bar: LayoutBar }[] {
  const out: { x: number; bar: LayoutBar }[] = [];
  const secs = l.sections?.length ? l.sections : [l.w];
  for (const bar of l.bars ?? []) {
    if (!bar.mid) continue;
    let sx = 0;
    for (const w of secs) {
      for (const x of [sx + SUPPORT_IN, sx + w - SUPPORT_IN])
        if (x - SUPPORT_W / 2 >= bar.x && x + SUPPORT_W / 2 <= bar.x + bar.w && !out.some((o) => o.bar === bar && Math.abs(o.x - x) < SUPPORT_W)) out.push({ x, bar });
      sx += w;
    }
  }
  return out;
}

export interface CopperPiece {
  pi: number;
  /** what it is, e.g. "Ana bara 1", "-Q1 üst ayak", "Kuplaj bağlantısı" */
  part: string;
  ph: string;
  /** one bar, e.g. "80×10" */
  size: string;
  /** bars of this length (parallel bars included) */
  count: number;
  /** 90° bends */
  bends: number;
  /** sum of the straight runs along the bar's centre line, mm */
  straight: number;
  /** developed length to cut, mm */
  cut: number;
}

/**
 * Copper pieces of the board layouts with their developed length for cutting: every 90° bend (inner radius the bar
 * thickness) shortens the centre-line length by (2 − π/2)·(r + t/2) ≈ 0.64·t.
 */
export function copperCutList(pr: Project, A: Analysis, parts: PartLookup = catOf): CopperPiece[] {
  const out: CopperPiece[] = [];
  const piece = (pi: number, part: string, ph: string, spec: string, straight: number, bends: number, times = 1) => {
    const d = busbarDims(spec), r = d.t;
    out.push({ pi, part, ph, size: `${d.w}×${d.t}`, count: d.n * times, bends, straight: Math.round(straight), cut: Math.round(straight - bends * (2 - Math.PI / 2) * (r + d.t / 2)) });
  };
  const runLen = (pts: [number, number][]) => pts.slice(1).reduce((t, p, i) => t + Math.abs(p[0] - pts[i][0]) + Math.abs(p[1] - pts[i][1]), 0);
  pr.pages.forEach((p, pi) => {
    const l = p.layout;
    if (!l || l.door) return;
    (l.bars ?? []).forEach((bar, bi) => {
      const name = $t('Ana bara {0}', bi + 1);
      for (const ph of ['L1', 'L2', 'L3']) piece(pi, name, ph, bar.spec, bar.w, 0);
      const [n0, n1] = bar.mid?.nSpan ?? [bar.x, bar.x + bar.w], [e0, e1] = bar.mid?.peSpan ?? [bar.x, bar.x + bar.w];
      piece(pi, name, 'N', bar.n ?? bar.spec, n1 - n0, 0);
      piece(pi, name, 'PE', bar.pe, e1 - e0, 0);
    });
    for (const k of l.links ?? []) {
      const name = k.kind === 'N' ? $t('Nötr bağlantısı') : $t('Kuplaj bağlantısı');
      if (k.kind === 'N' || k.ph != null) piece(pi, name, k.kind === 'N' ? 'N' : `L${k.ph! + 1}`, k.spec, runLen(k.pts), k.pts.length - 2);
      else piece(pi, name, 'L1 L2 L3', k.spec, runLen(k.pts), k.pts.length - 2, 3);
    }
    const devs = layoutDevices(A, parts), fed = busFed(pr, A), midBar = l.bars?.find((b) => b.mid);
    for (const it of l.items) {
      const d = devs.get(it.dev);
      if (!d || !footed(A, d)) continue;
      const fs = footSpec(d), [ty, by] = terminalYs(d);
      const bar = l.bars?.find((b) => onBar(b, it.x + d.w / 2)) ?? midBar, onBus = !!bar && fed(it.dev), under = !!bar && it.y > bar.y;
      const bw = bar ? busbarDims(bar.spec).w : 0, pitch = bar ? barPitch(bar.spec) : 0;
      const drop = !bar ? 0 : under ? it.y - FOOT - (bar.y + bw / 2) : bar.y - bw / 2 - (it.y + d.h + FOOT);
      for (const f of feet(it.x, d)) {
        const k = f.ph === 'N' ? -1 : +f.ph[1] - 1, turn = onBus && k >= 0 ? 40 + k * pitch : 0;
        // the foot on the busbar side turns forward to its phase's depth (one bend), the other runs straight
        piece(pi, `${d.name} ${$t('üst ayak')}`, f.ph, fs, ty + FOOT + (under ? turn : 0), under && turn ? 1 : 0);
        piece(pi, `${d.name} ${$t('alt ayak')}`, f.ph, fs, d.h - by + FOOT + (!under ? turn : 0), !under && turn ? 1 : 0);
        if (onBus && k >= 0 && drop > 0) piece(pi, `${d.name} ${$t('iniş')}`, f.ph, fs, drop, 0);
      }
    }
  });
  return out;
}

export function layoutMaterials(pr: Project, A?: Analysis, parts: PartLookup = catOf) {
  let rail = 0, supports = 0;
  const ducts = new Map<number, number>();
  // copper per bar size ("80×10"), mm: the four phase / neutral conductors and the PE conductor of each busbar system
  const copper = new Map<string, number>();
  for (const p of pr.pages) {
    if (!p.layout || p.layout.door) continue;
    for (const bar of p.layout.bars ?? [])
      for (const [spec, conductors] of [[bar.spec, 4], [bar.pe, 1]] as const) {
        const d = busbarDims(spec), size = `${d.w}×${d.t}`;
        copper.set(size, (copper.get(size) ?? 0) + bar.w * conductors * d.n);
      }
    // connection bars (coupler, neutral): their path length, per phase conductor, each bar of the pack
    for (const k of p.layout.links ?? []) {
      const d = busbarDims(k.spec), size = `${d.w}×${d.t}`, conductors = k.kind || k.ph != null ? 1 : 3;
      let len = 0;
      for (let n = 0; n < k.pts.length - 1; n++) len += Math.abs(k.pts[n + 1][0] - k.pts[n][0]) + Math.abs(k.pts[n + 1][1] - k.pts[n][1]);
      copper.set(size, (copper.get(size) ?? 0) + len * conductors * d.n);
    }
    supports += barSupports(p.layout).length;
    // copper on the breakers (with the analysis): the terminal bars up and down beyond the breaker, the turn forward to
    // the phase bar, and the droppers from the phase bars, in the breaker's bar size and number of parallel bars
    if (A) {
      const devs = layoutDevices(A, parts), fed = busFed(pr, A), midBar = p.layout.bars?.find((b) => b.mid);
      for (const it of p.layout.items) {
        const d = devs.get(it.dev);
        if (!d || !footed(A, d)) continue;
        const fs = footSpec(d), bd = busbarDims(fs), size = `${bd.w}×${bd.t}`, [ty, by] = terminalYs(d), f = feet(it.x, d);
        let len = f.length * (ty + FOOT + (d.h - by) + FOOT);
        const bar = p.layout.bars?.find((b) => onBar(b, it.x + d.w / 2)) ?? midBar;
        if (bar && fed(it.dev)) {
          const bw = busbarDims(bar.spec).w, down = it.y > bar.y;
          const drop = down ? it.y - FOOT - (bar.y + bw / 2) : bar.y - bw / 2 - (it.y + d.h + FOOT);
          // per phase: the dropper and the turn forward to its depth (L1 … L3 one pitch further each)
          for (let k = 0; k < 3; k++) len += Math.max(0, drop) + 40 + k * barPitch(bar.spec);
        }
        copper.set(size, (copper.get(size) ?? 0) + len * bd.n);
      }
    }
    for (const r of p.layout.rails) rail += r.len;
    for (const d of p.layout.ducts) {
      const size = Math.round(Math.min(d.w, d.h)), len = Math.max(d.w, d.h);
      ducts.set(size, (ducts.get(size) ?? 0) + len);
    }
  }
  return { rail, ducts: [...ducts.entries()].sort((a, b) => a[0] - b[0]), copper: [...copper.entries()], supports };
}

/* ---------------- automatic layout ---------------- */

/** Door-mounted devices (operator panel), left off the plate unless asked for. */
const DOOR = new Set(['pb_no', 'pb_nc', 'pb_latch', 'estop', 'estop_no', 'rot01', 'rot012', 'lamp', 'lamp_led', 'gctl', 'hmi', 'horn']);

/** Is the device an operator device that belongs on the door (button, lamp, emergency stop)? */
export const isDoorDevice = (A: Analysis, key: string) => {
  const it = A.mains.get(key)?.[0];
  return !!it && DOOR.has(fnOf(it.s.type));
};

/** Cabinet order: incoming supply → protection → switching → electronics → other → terminals. */
function rank(A: Analysis, key: string): number {
  const it = A.mains.get(key)?.[0] ?? A.all.find((x) => A.devKey.get(x.s.id) === key);
  const t = fnOf(it?.s.type ?? '');
  if (['cb1', 'fuse'].includes(t)) return 0;
  if (['cb3', 'ol3'].includes(t)) return 1;
  if (t === 'coil' || t === 'coil_on' || t === 'coil_off') return 2;
  if (t === 'psu') return 3;
  if (LIB[t]?.role === 'plc') return 4;
  if (LIB[t]?.role === 'term') return 9;
  return 5;
}

export interface AutoLayoutOptions {
  /** 'fresh' rebuilds the page; 'fill' keeps what is there and adds the unplaced devices */
  mode: 'fresh' | 'fill';
  /** include door devices (buttons, lamps) */
  includeDoor?: boolean;
  /** enlarge the plate height when devices do not fit */
  growPlate?: boolean;
  margin?: number;
  /** wire duct size (height of horizontal, width of vertical ducts) */
  duct?: number;
  /** horizontal gap between devices */
  gap?: number;
}

export interface AutoLayoutResult {
  placed: string[];
  skipped: string[];
  rows: number;
  grewTo?: number;
  /** floor-standing board: where each compartment group lies and which busbar section it is on */
  spans?: { x: number; w: number; bus: number; name: string }[];
}

/**
 * Places the devices that are not on any layout page yet (in 'fresh' mode also
 * the ones already on this page) in rows on DIN rails with wire ducts between.
 */
export function autoLayout(pr: Project, A: Analysis, pi: number, o: AutoLayoutOptions, parts: PartLookup = catOf): AutoLayoutResult {
  const page = pr.pages[pi], l = page.layout;
  if (!l) throw new Error($t("Bu sayfa bir pano yerleşimi değil."));
  const margin = o.margin ?? 20, D = o.duct ?? 40, gap = o.gap ?? 5, rowGap = 10;
  const devs = layoutDevices(A, parts);
  const elsewhere = new Set(pr.pages.flatMap((p, i) => (i !== pi && p.layout ? p.layout.items.map((x) => x.dev) : [])));
  if (o.mode === 'fresh') {
    l.items = [];
    l.rails = [];
    l.ducts = [];
  }
  const here = new Set(l.items.map((x) => x.dev));
  const doorKey = (k: string) => {
    const it = A.mains.get(k)?.[0];
    return !!it && DOOR.has(fnOf(it.s.type));
  };
  const todo = [...devs.values()]
    .filter((d) => !elsewhere.has(d.key) && !here.has(d.key) && (l.door ? doorKey(d.key) : o.includeDoor || !doorKey(d.key)))
    .sort((a, b) => rank(A, a.key) - rank(A, b.key) || a.name.localeCompare(b.name, 'tr', { numeric: true }));
  const res: AutoLayoutResult = { placed: [], skipped: [], rows: 0 };
  // an inside view holds no devices of its own
  if (!todo.length || l.inside) return res;
  if (l.door) return doorLayout(l, A, todo, res, o.growPlate ?? false);

  const x0 = margin + D + gap, x1 = l.w - margin - D - gap;
  const rects = () => [
    ...l.items.map((i) => {
      const d = devs.get(i.dev);
      return [i.x, i.y, i.x + (d?.w ?? 0), i.y + (d?.h ?? 0)] as [number, number, number, number];
    }),
    ...l.ducts.map((d) => [d.x, d.y, d.x + d.w, d.y + d.h] as [number, number, number, number]),
  ];
  const free = (r: [number, number, number, number]) => r[0] >= 0 && r[1] >= 0 && r[2] <= l.w && r[3] <= l.h && !rects().some((q) => r[0] < q[2] && q[0] < r[2] && r[1] < q[3] && q[1] < r[3]);
  const place = (d: LayoutDevice, x: number, y: number) => {
    l.items.push({ id: newId(), dev: d.key, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
    res.placed.push(d.name);
  };

  // 'fill': first try the gaps on existing rails
  let queue = todo;
  if (o.mode === 'fill' && l.rails.length) {
    const left: LayoutDevice[] = [];
    for (const d of queue) {
      let done = false;
      for (const r of l.rails) {
        for (let x = Math.max(r.x, 0); x + d.w <= r.x + r.len && !done; x += 5) {
          const y = r.y - d.h / 2;
          if (free([x - gap / 2, y, x + d.w + gap / 2, y + d.h])) {
            place(d, x, y);
            done = true;
          }
        }
        if (done) break;
      }
      if (!done) left.push(d);
    }
    queue = left;
  }
  if (!queue.length) return res;

  // new rows below whatever is on the plate: duct, rail row, duct, rail row, …
  let top = margin;
  for (const q of rects()) top = Math.max(top, q[3] + rowGap);
  if (o.mode === 'fresh' || !l.ducts.some((d) => d.h > d.w)) {
    // vertical side ducts over the full usable height (extended below if the plate grows)
    l.ducts.push({ id: newId(), x: margin, y: margin, w: D, h: l.h - 2 * margin }, { id: newId(), x: l.w - margin - D, y: margin, w: D, h: l.h - 2 * margin });
  }
  const rows: LayoutDevice[][] = [];
  let row: LayoutDevice[] = [], width = 0, lastRank = -1;
  for (const d of queue) {
    const r = rank(A, d.key);
    const tooWide = d.w > x1 - x0;
    if (tooWide) {
      res.skipped.push(d.name);
      continue;
    }
    // terminals always start their own row
    if (row.length && (width + gap + d.w > x1 - x0 || (r === 9 && lastRank !== 9))) {
      rows.push(row);
      row = [];
      width = 0;
    }
    width += (row.length ? gap : 0) + d.w;
    row.push(d);
    lastRank = r;
  }
  if (row.length) rows.push(row);

  let y = top;
  for (const r of rows) {
    const h = Math.max(...r.map((d) => d.h));
    const need = y + D + rowGap + h + rowGap;
    if (need > l.h - margin) {
      if (o.growPlate) {
        l.h = Math.ceil((need + margin) / 50) * 50;
        res.grewTo = l.h;
        for (const d of l.ducts) if (d.h > d.w) d.h = l.h - 2 * margin;
      } else {
        r.forEach((d) => res.skipped.push(d.name));
        continue;
      }
    }
    l.ducts.push({ id: newId(), x: margin + D, y, w: l.w - 2 * (margin + D), h: D });
    const railY = y + D + rowGap + h / 2;
    l.rails.push({ id: newId(), x: margin + D, y: Math.round(railY * 10) / 10, len: l.w - 2 * (margin + D) });
    let x = x0;
    for (const d of r) {
      place(d, x, railY - d.h / 2);
      x += d.w + gap;
    }
    y = railY + h / 2 + rowGap;
    res.rows++;
  }
  // closing duct under the last row when there is room
  if (res.rows && y + D <= l.h - margin) l.ducts.push({ id: newId(), x: margin + D, y, w: l.w - 2 * (margin + D), h: D });
  if (res.grewTo) l.scale = Math.max(l.scale, fitScale(l.w, l.h));
  return res;
}

/** Mounting plate height of a floor-standing cabinet (2000 mm); a board needing more gets further cabinets. */
export const PLATE_MAX_H = 1800;
const PLATE_WIDTHS = [600, 800, 1000, 1200];
/** Standard inner depths of cabinets, mm. */
export const CABINET_DEPTHS = [250, 300, 400, 500, 600];

/**
 * Cabinet depth for a plate: the deepest device (on its DIN rail) with room in front of it for the door devices'
 * contact blocks; a standard depth, the deepest one when nothing smaller fits.
 */
export function depthForDevices(l: PanelLayout, devs: Map<string, LayoutDevice>): number {
  const need = Math.max(0, ...l.items.map((i) => (devs.get(i.dev)?.dp ?? 80) + RAIL_D + 50));
  return CABINET_DEPTHS.find((v) => v >= need) ?? CABINET_DEPTHS[CABINET_DEPTHS.length - 1];
}
const RAIL_D = 7.5;

/**
 * Mounting plates for the devices not on a layout page yet. One plate, grown to fit, as long as it stays within a
 * floor-standing cabinet: 600 mm wide, else the narrowest wider plate (up to 1200 mm) that takes everything; else
 * cabinets of 1200 × 1800 mm side by side until everything is placed. The pages are added to the project.
 */
export function addPlates(pr: Project, parts: PartLookup = catOf): { pages: Page[]; skipped: string[] } {
  const A = analyze(pr);
  const attempt = (w: number, h: number, grow: boolean) => {
    const page: Page = { id: newId(), desc: '', kind: 'layout', layout: blankLayout(w, h), syms: [], wires: [] };
    pr.pages.push(page);
    return { page, r: autoLayout(pr, A, pr.pages.length - 1, { mode: 'fresh', growPlate: grow }, parts) };
  };
  const devs = layoutDevices(A, parts);
  const deep = (pages: Page[]) => pages.forEach((p) => (p.layout!.depth = depthForDevices(p.layout!, devs)));
  for (const w of PLATE_WIDTHS) {
    const { page, r } = attempt(w, 800, true);
    if (page.layout!.h <= PLATE_MAX_H && !r.skipped.length) {
      deep([page]);
      return { pages: [page], skipped: [] };
    }
    pr.pages.pop();
  }
  const pages: Page[] = [];
  for (let n = 0; n < 30; n++) {
    const { page, r } = attempt(PLATE_WIDTHS[PLATE_WIDTHS.length - 1], PLATE_MAX_H, false);
    if (!r.placed.length) {
      pr.pages.pop();
      deep(pages);
      return { pages, skipped: r.skipped };
    }
    pages.push(page);
    if (!r.skipped.length) break;
  }
  deep(pages);
  return { pages, skipped: [] };
}

/** The inside view of a door page: the same size, showing the front view's devices mirrored. */
export function doorInside(front: Page): Page {
  const l = front.layout!;
  return { id: newId(), desc: '', kind: 'layout', layout: { ...blankLayout(l.w, l.h, true), inside: front.id }, syms: [], wires: [] };
}

/** Door sizes for mounting plates: the plate's own size, or one door per compartment of a floor-standing board. */
export function doorSizes(plates: PanelLayout[]): [number, number][] {
  return plates.flatMap((l): [number, number][] => (l.sections?.length ? l.sections.map((w) => [w, l.h]) : [[l.w, l.h]]));
}

/**
 * Doors for the door devices (buttons, lamps, meters, controllers) not on a layout page yet: each door is the size of
 * its board (or compartment), in the order of the plates; a door that is full passes the rest on to the next one.
 * Doors never grow beyond their board. The pages are added to the project; what fits on no door is returned.
 */
export function addDoors(pr: Project, plates: PanelLayout[], parts: PartLookup = catOf): { pages: { front: Page; inside: Page }[]; skipped: string[] } {
  const A = analyze(pr), pages: { front: Page; inside: Page }[] = [];
  let skipped: string[] = [];
  for (const [w, h] of doorSizes(plates)) {
    const page: Page = { id: newId(), desc: '', kind: 'layout', layout: blankLayout(w, h, true), syms: [], wires: [] };
    pr.pages.push(page);
    const r = autoLayout(pr, A, pr.pages.length - 1, { mode: 'fresh', growPlate: false }, parts);
    if (!r.placed.length) pr.pages.pop();
    else {
      // each door also seen from inside (mirrored), right after its front view
      const inside = doorInside(page);
      pr.pages.push(inside);
      pages.push({ front: page, inside });
    }
    skipped = r.skipped;
    // nothing left for the door, or a door that took nothing: the next one would take the same
    if (!skipped.length) break;
  }
  return { pages, skipped };
}

/* ---------------- compartments of a floor-standing board ---------------- */

/** Standard compartment widths of floor-standing boards (mm). */
export const SECTION_WIDTHS = [400, 600, 800, 1000, 1200];
/** Device weight one compartment is laid out for (mounting plate and frame), kg. */
export const SECTION_MAX_KG = 250;
/** From this weight or width a device gets a compartment of its own (air circuit breakers, big switch-disconnectors). */
const OWN_KG = 25, OWN_W = 280;
/** Air circuit breakers up to this rating share a compartment, two to one (one under, one over the busbars). */
export const STACK_MAX_A = 2000;
/** Heavy devices (moulded case breakers, capacitors) sit low, in rows from the bottom up. */
const HEAVY_KG = 1.5, HEAVY_H = 140;

/** Device weight per compartment (by the device centre); one entry when the plate has no compartments. */
export function sectionWeights(l: PanelLayout, devs: Map<string, LayoutDevice>): number[] {
  const secs = l.sections?.length ? l.sections : [l.w];
  const out = secs.map(() => 0);
  for (const it of l.items) {
    const d = devs.get(it.dev);
    if (!d?.kg) continue;
    let x = 0, n = 0;
    const cx = it.x + d.w / 2;
    while (n < secs.length - 1 && cx > x + secs[n]) x += secs[n++];
    out[n] += d.kg;
  }
  return out.map((v) => Math.round(v * 10) / 10);
}

/** From this weight a breaker stands on cross beams (atkı), not on the mounting plate, with copper feet on its terminals. */
export const BEAM_KG = 10;
/** Cross beam height in the front view and its depth, mm. */
export const BEAM_H = 40, BEAM_D = 40;
/** Copper feet on the terminals of heavy breakers, and the free space under the feet for connecting the cables, mm. */
export const FOOT = 100, CABLE_GAP = 200;
const FEET_TYPES = new Set(['cb3m', 'mccb3', 'lbs3']);

const typeOf = (A: Analysis, key: string) => fnOf(A.mains.get(key)?.[0]?.s.type ?? '');
/** A heavy breaker: on cross beams, copper feet top and bottom, cable space under it. */
export const footed = (A: Analysis, d: LayoutDevice) => (d.kg ?? 0) >= BEAM_KG && FEET_TYPES.has(typeOf(A, d.key));
/** A device centre over this busbar (several busbar sections lie side by side). */
export const onBar = (bar: { x: number; w: number }, cx: number) => cx >= bar.x && cx <= bar.x + bar.w;

/** Breakers / switches connected by copper to the main busbars (a pin on a wire drawn as busbar); all of them when nothing is marked. */
export function busFed(pr: Project, A: Analysis): (key: string) => boolean {
  const at = new Set(pr.pages.flatMap((p, i) => p.wires.filter((w) => w.bus).flatMap((w) => w.pts.map((q) => `${i}:${q[0]},${q[1]}`))));
  const fed = new Set<string>();
  pr.pages.forEach((p, i) => {
    for (const s of p.syms) if (pinsW(s).some((q) => at.has(`${i}:${q[0]},${q[1]}`))) fed.add(A.devKey.get(s.id) ?? '');
  });
  return (key) => FEET_TYPES.has(typeOf(A, key)) && (!at.size || fed.has(key));
}

export interface SectionLayoutOptions {
  /** first usable height (default: the margin) */
  top?: number;
  /** height band kept free across all compartments (main phase busbars in the middle of the board) */
  band?: [number, number];
  /** lowest usable height (the N and PE busbars are below) */
  bottom?: number;
  /**
   * compartment groups from left to right: the devices drawn on `pages` go into compartments of their
   * own, named `name`; `bus` is the busbar section (-1: a coupler between two sections)
   */
  groups?: { name: string; bus: number; pages: string[] }[];
}

/**
 * Lays out a floor-standing board in compartments by the size and weight of the devices: every
 * air circuit breaker (or other device over 25 kg / 280 mm) gets a compartment of its own, placed
 * low; moulded case breakers and other heavy devices fill rows from the bottom up, heaviest first;
 * modular devices go on DIN rails from the top down. Breakers of BEAM_KG and more stand on cross
 * beams with copper feet above and below and CABLE_GAP free under the lower feet. The `band` (main
 * busbars) splits each compartment into an upper and a lower part. A compartment is closed when it is
 * full or its device weight would pass SECTION_MAX_KG. The plate height stays; the width is the sum
 * of the standard compartment widths.
 */
export function sectionLayout(pr: Project, A: Analysis, pi: number, o: SectionLayoutOptions = {}, parts: PartLookup = catOf): AutoLayoutResult {
  const l = pr.pages[pi].layout;
  if (!l || l.door) throw new Error($t("Bu sayfa bir pano yerleşimi değil."));
  const margin = 20, D = 40, gap = 5, rowGap = 10, side = 2 * (margin + D + gap);
  const top = Math.max(margin, o.top ?? margin), bottom = Math.min(l.h - margin, o.bottom ?? l.h - margin);
  const zones: [number, number][] = o.band && o.band[0] > top && o.band[1] < bottom ? [[top, o.band[0]], [o.band[1], bottom]] : [[top, bottom]];
  const devs = layoutDevices(A, parts);
  const fedBy = busFed(pr, A);
  const elsewhere = new Set(pr.pages.flatMap((p, i) => (i !== pi && p.layout ? p.layout.items.map((x) => x.dev) : [])));
  l.items = [];
  l.rails = [];
  l.ducts = [];
  l.beams = [];
  const res: AutoLayoutResult = { placed: [], skipped: [], rows: 0 };
  const kg = (d: LayoutDevice) => d.kg ?? 0;
  const ampsOf = (d: LayoutDevice) => (d.pn ? ratedOf(parts(d.pn)?.d ?? '') ?? 0 : 0);
  const byName = (a: LayoutDevice, b: LayoutDevice) => a.name.localeCompare(b.name, 'tr', { numeric: true });
  const todo = [...devs.values()].filter((d) => !elsewhere.has(d.key) && !isDoorDevice(A, d.key));
  // terminal strips are long but light: they stay on the rails with the modular devices
  const ownAll = todo.filter((d) => !d.strip && (kg(d) >= OWN_KG || d.w >= OWN_W)).sort(byName);
  const heavyAll = todo.filter((d) => !d.strip && !ownAll.includes(d) && (kg(d) >= HEAVY_KG || d.h >= HEAVY_H)).sort((a, b) => kg(b) - kg(a) || byName(a, b));
  const lightAll = todo.filter((d) => !ownAll.includes(d) && !heavyAll.includes(d)).sort((a, b) => rank(A, a.key) - rank(A, b.key) || byName(a, b));
  const widthFor = (w: number, min: number) => SECTION_WIDTHS.find((s) => s >= min && s - side >= w);

  // each compartment has one zone per free height range; `hi` fills from the top, `lo` from the bottom
  // rails: control wiring in this zone (only there wire ducts: power cables run free in a power board)
  interface Zone { top: number; hi: number; lo: number; rails?: boolean }
  // solo: a big air circuit breaker's compartment, nothing else on its beams (metering may still go on a rail)
  interface Sec { x: number; w: number; kg: number; own: boolean; g: number; z: Zone[]; solo?: boolean; rails?: boolean }
  const secs: Sec[] = [];
  let curG = 0;
  const open = (w: number, own = false): Sec => {
    const s = { x: secs.reduce((a, q) => a + q.w, 0), w, kg: 0, own, g: curG, z: zones.map(([a, b]) => ({ top: a, hi: a, lo: b })) };
    secs.push(s);
    return s;
  };
  const inner = (s: Sec) => s.w - side;
  const r1 = (v: number) => Math.round(v * 10) / 10;
  const hduct = (s: Sec, y: number) => l.ducts.push({ id: newId(), x: s.x + margin + D, y: r1(y), w: s.w - 2 * (margin + D), h: D });
  const beam = (s: Sec, y: number) => l.beams!.push({ id: newId(), x: s.x + margin + D, y: r1(y), w: s.w - 2 * (margin + D) });
  const put = (d: LayoutDevice, x: number, y: number) => {
    l.items.push({ id: newId(), dev: d.key, x: r1(x), y: r1(y) });
    res.placed.push(d.name);
  };
  const rowsOf = (list: LayoutDevice[], width: number, apart: (a: LayoutDevice, b: LayoutDevice) => boolean) => {
    const rows: LayoutDevice[][] = [];
    let row: LayoutDevice[] = [], used = 0;
    for (const d of list) {
      if (d.w > width) {
        res.skipped.push(d.name);
        continue;
      }
      if (row.length && (used + gap + d.w > width || apart(row[row.length - 1], d))) {
        rows.push(row);
        row = [];
        used = 0;
      }
      used += (row.length ? gap : 0) + d.w;
      row.push(d);
    }
    if (row.length) rows.push(row);
    return rows;
  };
  const weightOk = (s: Sec, add: number) => s.kg === 0 || s.kg + add <= SECTION_MAX_KG;
  // the beams carry the breaker between its rear terminals (near its top and bottom edge, where the copper leaves)
  const beamPair = (s: Sec, top: number, bottomY: number) => {
    const inset = Math.max(20, (bottomY - top) * 0.3);
    beam(s, top + inset);
    beam(s, bottomY - inset - BEAM_H);
  };
  /**
   * Room for a row in a compartment. Breakers on beams stand right next to the main busbars, their copper
   * feet reaching the bars: under the bars (feet up, cables below) or over them (feet down, cables above),
   * with the cable space on the other side. Other rows fill the lowest zone from the bottom up over a duct.
   */
  const placeRow = (s: Sec, r: LayoutDevice[]) => {
    const h = Math.max(...r.map((d) => d.h)), hmin = Math.min(...r.map((d) => d.h)), rkg = r.reduce((a, d) => a + kg(d), 0);
    if (!weightOk(s, rkg)) return null;
    const widthOf = r.reduce((a, d) => a + d.w, 0) + gap * (r.length - 1);
    const x0 = s.x + margin + D + gap + (inner(s) - widthOf) / 2;
    const done = () => {
      s.kg += rkg;
      res.rows++;
    };
    if (r.some((d) => footed(A, d))) {
      // below the bars first (heavy low), then above them; without bars from the top of the plate
      const under = s.z[s.z.length - 1], over = s.z.length > 1 ? s.z[0] : null;
      const yu = under.hi + FOOT;
      if (yu + h + FOOT + CABLE_GAP <= under.lo)
        return () => {
          let x = x0;
          for (const d of r) {
            put(d, x, yu);
            x += d.w + gap;
          }
          beamPair(s, yu, yu + hmin);
          under.hi = yu + h + FOOT + CABLE_GAP;
          done();
        };
      const yb = over ? over.lo - FOOT : 0;
      if (over && yb - h - FOOT - CABLE_GAP >= over.hi)
        return () => {
          let x = x0;
          for (const d of r) {
            put(d, x, yb - d.h);
            x += d.w + gap;
          }
          beamPair(s, yb - hmin, yb);
          over.lo = yb - h - FOOT - CABLE_GAP;
          done();
        };
      return null;
    }
    // breakers fed by copper from the busbars hang right under them (short droppers), the rest fill from the bottom
    if (o.band && r.every((d) => fedBy(d.key))) {
      const under = s.z[s.z.length - 1];
      if (under.hi + rowGap + h + rowGap <= under.lo)
        return () => {
          const yt = under.hi + rowGap;
          let x = x0;
          for (const d of r) {
            put(d, x, yt);
            x += d.w + gap;
          }
          under.hi = yt + h + rowGap;
          done();
        };
    }
    // breaker rows without a wire duct: their power cables run free
    const zn = [...s.z].reverse().find((z) => z.lo - rowGap - h - rowGap >= z.hi);
    if (!zn) return null;
    return () => {
      const yb = zn.lo - rowGap;
      let x = x0;
      for (const d of r) {
        put(d, x, yb - d.h);
        x += d.w + gap;
      }
      zn.lo = yb - h - rowGap;
      done();
    };
  };

  // devices by group (board section and function: incomers, feeders, coupler), each group in compartments of
  // its own, in the given order; without groups everything is one group
  const groups = o.groups?.length ? o.groups : [{ name: '', bus: 0, pages: [] as string[] }];
  const at = new Map<string, number>();
  groups.forEach((g, gi) => g.pages.forEach((id) => at.set(id, gi)));
  const cpl = groups.findIndex((g) => g.bus < 0), spare = cpl >= 0 ? cpl : groups.length - 1;
  const pageOf = new Map<string, number>();
  for (const it of A.all) {
    const k = A.devKey.get(it.s.id);
    if (k && !pageOf.has(k)) pageOf.set(k, it.pi);
  }
  const groupOf = (d: LayoutDevice) => at.get(pr.pages[A.mains.get(d.key)?.[0]?.pi ?? pageOf.get(d.key) ?? -1]?.id ?? '') ?? spare;
  const fill = (own: LayoutDevice[], heavy: LayoutDevice[], light: LayoutDevice[]) => {
    // 1. big devices (air circuit breakers) in compartments of their own, two to a compartment: under and over the
    // busbars up to STACK_MAX_A (Lujo, Lord Palace); bigger ones alone, from 4000 A in a 1000 mm compartment (Esenyurt)
    let ownSec: Sec | null = null;
    for (const d of own) {
      const big = ampsOf(d) > STACK_MAX_A;
      let go = !big && ownSec && ownSec.w - side >= d.w ? placeRow(ownSec, [d]) : null;
      if (!go) {
        // a coupler's compartment also takes the riser to the next busbar beside the breaker
        const w = widthFor(d.w + (groups[curG]?.bus < 0 ? 160 : 0), ampsOf(d) >= 4000 ? 1000 : 800);
        const s = w ? open(w, true) : null;
        go = s && placeRow(s, [d]);
        if (!go) {
          if (s) secs.pop();
          res.skipped.push(d.name);
          continue;
        }
        ownSec = big ? null : s;
        if (big && s) s.solo = true;
      }
      go();
    }

    // 2. heavy devices in rows from the bottom up (breakers on beams in rows of their own), 3. modular devices on rails from the top down
    const wide = Math.max(0, ...heavy.map((d) => d.w), ...light.map((d) => d.w));
    const feederW = widthFor(wide, 800) ?? SECTION_WIDTHS[SECTION_WIDTHS.length - 1];
    let cur: Sec | null = null;
    const fresh = (): Sec => (cur = open(feederW));
    for (const r of rowsOf(heavy, feederW - side, (a, b) => footed(A, a) !== footed(A, b))) {
      // first where this group still has room, then a new compartment; no breakers beside a big air circuit breaker
      const breakers = r.some((d) => FEET_TYPES.has(typeOf(A, d.key)));
      let go: (() => void) | null = null;
      for (const s of [cur, ...secs.filter((q) => q.g === curG && q !== cur)]) if (s && !(s.solo && breakers) && (go = placeRow(s, r))) break;
      if (!go) go = placeRow(fresh(), r);
      if (!go) {
        r.forEach((d) => res.skipped.push(d.name));
        continue;
      }
      go();
    }
    for (const r of rowsOf(light, feederW - side, (a, b) => rank(A, b.key) === 9 && rank(A, a.key) !== 9)) {
      const mine = secs.filter((x) => x.g === curG);
      // small devices of a group whose compartments are full (metering beside two air circuit breakers) wait for
      // free room anywhere on the board, as in the real boards; a group with no compartment yet opens one
      if (!railRow(r, [cur, ...mine.filter((x) => x !== cur)], mine.length ? null : fresh)) later.push({ r, x: mine[0]?.x ?? 0 });
    }
  };
  /** a row of modular devices on a rail with a duct above and below, in the first compartment with room */
  const railRow = (r: LayoutDevice[], cands: (Sec | null)[], fresh: (() => Sec) | null) => {
    const h = Math.max(...r.map((d) => d.h)), rkg = r.reduce((a, d) => a + kg(d), 0);
    // room for the row and a closing duct under it
    const rw = r.reduce((a, d) => a + d.w, 0) + gap * (r.length + 1);
    const slot = (s: Sec) => (weightOk(s, rkg) && rw <= inner(s) ? s.z.find((z) => z.hi + D + rowGap + h + rowGap + D <= z.lo) : undefined);
    let s = null as unknown as Sec, zn: Zone | undefined;
    for (const q of cands)
      if (q && (zn = slot(q))) {
        s = q;
        break;
      }
    if (!zn && fresh) zn = slot((s = fresh()));
    if (!zn) return false;
    hduct(s, zn.hi);
    zn.rails = s.rails = true;
    const railY = zn.hi + D + rowGap + h / 2;
    l.rails.push({ id: newId(), x: s.x + margin + D, y: r1(railY), len: s.w - 2 * (margin + D) });
    let x = s.x + margin + D + gap;
    for (const d of r) {
      put(d, x, railY - d.h / 2);
      x += d.w + gap;
    }
    s.kg += rkg;
    zn.hi = railY + h / 2 + rowGap;
    res.rows++;
    return true;
  };
  const later: { r: LayoutDevice[]; x: number }[] = [];
  groups.forEach((_, gi) => {
    const mine = (list: LayoutDevice[]) => (o.groups?.length ? list.filter((d) => groupOf(d) === gi) : list);
    curG = gi;
    fill(mine(ownAll), mine(heavyAll), mine(lightAll));
  });
  // small devices that found no room in their group: wherever there is room, else in a control compartment at the end
  let ctl: Sec | null = null;
  for (const { r, x } of later) {
    if (railRow(r, [...secs].sort((a, b) => Math.abs(a.x - x) - Math.abs(b.x - x)), null)) continue;
    curG = groups.length;
    if (!railRow(r, [ctl], () => (ctl = open(widthFor(r.reduce((a, d) => a + d.w, 0) + gap * (r.length + 1), 600) ?? SECTION_WIDTHS[SECTION_WIDTHS.length - 1])))) r.forEach((d) => res.skipped.push(d.name));
  }
  // closing duct under the last rail row of each zone
  for (const s of secs) for (const z of s.z) if (z.rails && z.hi + D <= z.lo + rowGap) hduct(s, z.hi);

  // compartments of breakers and modular devices that are far from full get the smallest standard width that
  // holds them (a lone 630 A moulded case breaker: 400 mm, not 800); everything to the right moves left
  const plan = secs.map((s) => {
    const its = l.items.filter((i) => i.x >= s.x && i.x < s.x + s.w);
    if (s.own || !its.length) return { s, x0: s.x, w0: s.w, w: s.w, dx: 0 };
    const left = Math.min(...its.map((i) => i.x)), right = Math.max(...its.map((i) => i.x + (devs.get(i.dev)?.w ?? 0)));
    const w = SECTION_WIDTHS.find((sw) => sw - side >= right - left + 2 * gap) ?? s.w;
    return { s, x0: s.x, w0: s.w, w: Math.min(w, s.w), dx: s.x + margin + D + gap + (Math.min(w, s.w) - side - (right - left)) / 2 - left };
  });
  let before = 0;
  const secAt = (x: number) => plan.find((q) => x >= q.x0 && x < q.x0 + q.w0);
  const shiftOf = new Map(plan.map((q) => { const sh = before; before += q.w0 - q.w; return [q, sh] as const; }));
  if (before > 0) {
    for (const it of l.items) {
      const q = secAt(it.x);
      if (q) it.x = r1(it.x - shiftOf.get(q)! + (q.w < q.w0 ? q.dx : 0));
    }
    for (const r of l.rails) {
      const q = secAt(r.x);
      if (q) { r.x = r1(r.x - shiftOf.get(q)!); r.len -= q.w0 - q.w; }
    }
    for (const b of l.beams!) {
      const q = secAt(b.x);
      if (q) { b.x = r1(b.x - shiftOf.get(q)!); b.w -= q.w0 - q.w; }
    }
    for (const d of l.ducts) {
      const q = secAt(d.x);
      if (q) { d.x = r1(d.x - shiftOf.get(q)!); if (d.w > D) d.w -= q.w0 - q.w; }
    }
    for (const q of plan) {
      q.s.x = q.x0 - shiftOf.get(q)!;
      q.s.w = q.w;
    }
  }
  // where each group lies and on which busbar section (the control compartment with the last one)
  if (o.groups?.length) {
    const spans: (NonNullable<AutoLayoutResult['spans']>[number] & { g: number })[] = [];
    for (const s of secs) {
      const g = groups[s.g], last = spans[spans.length - 1];
      if (last && last.g === s.g) last.w += s.w;
      else spans.push({ x: s.x, w: s.w, bus: g?.bus ?? last?.bus ?? 0, name: g?.name ?? $t('Kumanda ve klemens'), g: s.g });
    }
    res.spans = spans.map(({ g: _g, ...sp }) => sp);
  }

  if (!secs.length) open(800);
  // side ducts of each compartment over the usable height
  // side ducts only in compartments with control wiring (rails)
  for (const s of secs) if (s.rails) l.ducts.push({ id: newId(), x: s.x + margin, y: top, w: D, h: bottom - top }, { id: newId(), x: s.x + s.w - margin - D, y: top, w: D, h: bottom - top });
  if (!l.beams.length) delete l.beams;
  l.sections = secs.map((s) => s.w);
  if (o.groups?.length) l.sectionNames = secs.map((s) => groups[s.g]?.name ?? $t('Kumanda ve klemens'));
  else delete l.sectionNames;
  l.w = secs.reduce((a, s) => a + s.w, 0);
  l.scale = Math.max(l.scale, fitScale(l.w, l.h));
  return res;
}
